import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useEffect, useState, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import { TravelTypes } from '../../../../constants';
import AddUpdateBrochure from '..';
import getBrochureTripId from '../../../../helpers/getBrochureTripId';
import useHandleCruiseAddToBrochure from '../../../../hooks/useHandleCruiseAddToBrochure';
import arePassengerCountsEqual from '../../../../helpers/checkArePassengerCountsEqual';

const CruiseAddUpdateToBrochure = ({
  tripId,
  className,
  cabinCategory,
  rateOption,
  itineraryDetail,
  selectedRoomIndex,
  tripRoomSearchInput,
}) => {
  const router = useRouter();
  const { brochure } = useSelector((state) => state.brochure);
  const [isSelected, setIsSelected] = useState(false);
  useAnalytics();

  const { staterooms: stateroomsObj } = useSelector((store) => store.tripSearch.searchObj);

  const { isViewingBrochure } = useSelector((state) => state.brochure);
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;

  const findRateOption = (rateOptions, rateOptionToFind) =>
    _.find(
      rateOptions,
      (c) =>
        c.rateOptionCode === rateOptionToFind.rateOptionCode &&
        c.rateOptionType === rateOptionToFind.rateOptionType &&
        c.nonRefundTypeCode === rateOptionToFind.nonRefundTypeCode,
    );

  const findRateCategory = useCallback(
    (cabinCategories, cabinCategoryToFind, staterooms) =>
      _.find(
        cabinCategories,
        (c) =>
          c.cabinCategoryCode === cabinCategoryToFind.cabinCategoryCode &&
          arePassengerCountsEqual(staterooms, tripRoomSearchInput || stateroomsObj),
      ),
    [stateroomsObj, tripRoomSearchInput],
  );

  const findCruiseItinerary = (trip) =>
    _.find(trip.tripSegments, (i) => i.itinerary.mode === TravelTypes.cruise);

  const currentBrochure = useSelector((state) => state.brochure.brochures[brochure && brochure.id]);
  const brochureOnBrochurePage = useSelector((state) => state.brochure.brochures[tripId]);

  const id = useMemo(
    () =>
      getBrochureTripId(
        tripId,
        brochure,
        itineraryDetail,
        tripRoomSearchInput || stateroomsObj,
        TravelTypes.cruise,
      ),
    [brochure, tripRoomSearchInput, itineraryDetail, stateroomsObj, tripId],
  );

  const setSelectedTripSegment = useCallback(
    (cruiseSeg, staterooms) => {
      if (
        cruiseSeg &&
        cruiseSeg.itinerary &&
        cruiseSeg.itinerary.itineraryDetail &&
        cruiseSeg.itinerary.itineraryDetail.itineraryId === (itineraryDetail || {}).itineraryId &&
        cruiseSeg.itinerary.cabinCategories &&
        cruiseSeg.itinerary.cabinCategories[selectedRoomIndex] &&
        cruiseSeg.itinerary.itineraryDetail?.itineraryDepartDate ===
          itineraryDetail?.itineraryDepartDate &&
        cruiseSeg.itinerary.itineraryDetail?.itineraryReturnDate ===
          itineraryDetail?.itineraryReturnDate
      ) {
        const selectedCategory = findRateCategory(
          cruiseSeg.itinerary.cabinCategories[selectedRoomIndex].cabinCategories,
          cabinCategory,
          staterooms,
        );
        if (selectedCategory != null) {
          const selectedRate = findRateOption(selectedCategory.rateOptions, rateOption);
          if (selectedRate) {
            setIsSelected(true);
          }
        }
      }
    },
    [cabinCategory, findRateCategory, itineraryDetail, rateOption, selectedRoomIndex],
  );

  useEffect(() => {
    setIsSelected(false);
    const findAndSetCruiseSegment = (brochureData, tripIdentifier) => {
      if (!brochureData || !brochureData.trips || brochureData.trips.length === 0) {
        return;
      }

      const isMatchingCruiseSegment = (segment, trip) => {
        const itinerary = segment?.itinerary;
        const detail = itinerary?.itineraryDetail;
        return (
          itinerary?.mode === TravelTypes.cruise &&
          detail?.itineraryId === itineraryDetail?.itineraryId &&
          detail?.itineraryDepartDate === itineraryDetail?.itineraryDepartDate &&
          detail?.itineraryReturnDate === itineraryDetail?.itineraryReturnDate &&
          arePassengerCountsEqual(
            tripRoomSearchInput || stateroomsObj,
            trip?.roomSearchInput ?? trip?.staterooms,
          )
        );
      };

      const isMatchingTrip = (trip) =>
        trip.id === tripIdentifier &&
        trip?.tripSegments?.some((segment) => isMatchingCruiseSegment(segment, trip));

      const foundTrip = brochureData.trips.find(isMatchingTrip);

      if (foundTrip && foundTrip.tripSegments) {
        const cruiseSegment = findCruiseItinerary(foundTrip);
        if (cruiseSegment) {
          setSelectedTripSegment(
            cruiseSegment,
            foundTrip?.staterooms ?? foundTrip?.roomSearchInput,
          );
        }
      }
    };

    if (isBrochurePage && tripId) {
      findAndSetCruiseSegment(brochureOnBrochurePage, tripId);
      return;
    }

    if (isViewingBrochure && currentBrochure) {
      findAndSetCruiseSegment(currentBrochure, brochure?.id ?? null);
      return;
    }

    if (brochure && brochure.trips && brochure.trips[id]) {
      const cruiseSegment = findCruiseItinerary(brochure.trips[id]);
      if (cruiseSegment) {
        setSelectedTripSegment(
          cruiseSegment,
          brochure?.trips?.[id]?.staterooms ?? brochure?.trips?.[id]?.roomSearchInput,
        );
      }
    }
  }, [
    brochure,
    brochureOnBrochurePage,
    currentBrochure,
    isBrochurePage,
    isViewingBrochure,
    itineraryDetail,
    setSelectedTripSegment,
    tripId,
    id,
    tripRoomSearchInput,
    stateroomsObj,
  ]);

  const handleAddToBrochureClick = useHandleCruiseAddToBrochure({
    tripId,
    itineraryDetail,
    brochure,
    isViewingBrochure,
    isBrochurePage,
    currentBrochure,
    brochureOnBrochurePage,
    cabinCategory,
    rateOption,
    selectedRoomIndex,
    tripRoomSearchInput,
  });
  return (
    <AddUpdateBrochure
      handleAddToBrochureClick={handleAddToBrochureClick}
      className={className}
      isSelected={isSelected}
    />
  );
};

CruiseAddUpdateToBrochure.propTypes = {
  tripId: PropTypes.string.isRequired,
  className: PropTypes.string,
  cabinCategory: PropTypes.shape().isRequired,
  rateOption: PropTypes.shape().isRequired,
  itineraryDetail: PropTypes.shape().isRequired,
  selectedRoomIndex: PropTypes.number.isRequired,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
};

CruiseAddUpdateToBrochure.defaultProps = {
  className: '',
  tripRoomSearchInput: undefined,
};
export default CruiseAddUpdateToBrochure;
